import type { Property } from '../types/property';

/**
 * Structured data types for Schema.org
 */

export interface StructuredData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

/**
 * Generate structured data for a Real Estate Property Listing
 * Follows Schema.org RealEstateListing format
 *
 * @see https://schema.org/RealEstateListing
 */
export function generatePropertyStructuredData(property: Property, baseUrl: string): StructuredData {
  const structuredData: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: property.title,
    description: property.description,
    url: `${baseUrl}/propiedad/${property.id}`,
  };

  // Add address if available
  if (property.address || property.city || property.province || property.country) {
    structuredData.address = {
      '@type': 'PostalAddress',
    };

    if (property.address) {
      structuredData.address.streetAddress = property.address;
    }
    if (property.city) {
      structuredData.address.addressLocality = property.city;
    }
    if (property.province) {
      structuredData.address.addressRegion = property.province;
    }
    if (property.country) {
      structuredData.address.addressCountry = property.country;
    }
  }

  // Add price information
  if (property.price) {
    structuredData.offers = {
      '@type': 'Offer',
      price: property.price,
      priceCurrency: property.currency || 'USD',
      availability: property.status === 'AVAILABLE'
        ? 'https://schema.org/InStock'
        : 'https://schema.org/SoldOut',
    };
  }

  // Add images
  if (property.images && property.images.length > 0) {
    structuredData.image = property.images.map(img => img.url);
  }

  // Add property details
  if (property.bedrooms) {
    structuredData.numberOfRooms = property.bedrooms;
    structuredData.numberOfBedrooms = property.bedrooms;
  }

  if (property.bathrooms) {
    structuredData.numberOfBathroomsTotal = property.bathrooms;
  }

  if (property.area) {
    structuredData.floorSize = {
      '@type': 'QuantitativeValue',
      value: property.area,
      unitCode: 'MTK', // Square meters
      unitText: 'm²',
    };
  }

  // Add property type
  if (property.type) {
    structuredData.additionalType = property.type;
  }

  // Add listing date
  if (property.createdAt) {
    structuredData.datePosted = new Date(property.createdAt).toISOString();
  }

  return structuredData;
}

/**
 * Generate structured data for Organization
 * Used on homepage and about pages
 *
 * @see https://schema.org/Organization
 */
export function generateOrganizationStructuredData(
  name: string,
  description: string,
  logo: string,
  url: string,
  contact?: {
    phone?: string;
    email?: string;
    address?: string;
  },
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  }
): StructuredData {
  const structuredData: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name,
    description,
    logo,
    url,
  };

  // Add contact information
  if (contact) {
    if (contact.phone) {
      structuredData.telephone = contact.phone;
    }
    if (contact.email) {
      structuredData.email = contact.email;
    }
    if (contact.address) {
      structuredData.address = {
        '@type': 'PostalAddress',
        streetAddress: contact.address,
      };
    }
  }

  // Add social media profiles
  if (social) {
    const sameAs: string[] = [];
    if (social.facebook) sameAs.push(social.facebook);
    if (social.instagram) sameAs.push(social.instagram);
    if (social.twitter) sameAs.push(social.twitter);
    if (social.linkedin) sameAs.push(social.linkedin);

    if (sameAs.length > 0) {
      structuredData.sameAs = sameAs;
    }
  }

  return structuredData;
}

/**
 * Generate Breadcrumb structured data
 * Used on all pages except homepage
 *
 * @see https://schema.org/BreadcrumbList
 */
export function generateBreadcrumbStructuredData(
  breadcrumbs: Array<{ name: string; url: string }>,
  baseUrl: string
): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${baseUrl}${crumb.url}`,
    })),
  };
}

/**
 * Generate SearchAction structured data
 * Used on homepage to enable site search in Google
 *
 * @see https://schema.org/SearchAction
 */
export function generateSearchActionStructuredData(baseUrl: string): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/ventas?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Generate CollectionPage structured data for property listings
 * Used on /ventas and /arriendos pages
 *
 * @see https://schema.org/CollectionPage
 */
export function generateCollectionPageStructuredData(
  name: string,
  description: string,
  url: string,
  numberOfItems?: number
): StructuredData {
  const structuredData: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url,
  };

  if (numberOfItems !== undefined) {
    structuredData.numberOfItems = numberOfItems;
  }

  return structuredData;
}

/**
 * Helper function to convert structured data to JSON-LD script
 */
export function structuredDataToScript(data: StructuredData | StructuredData[]): string {
  return JSON.stringify(data, null, 2);
}
